import { useState, useMemo } from 'react';
import { T, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { AuditModule } from '@erp/shared-types';
import DashboardLayout from '@/layouts/dashboard/DashboardLayout';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  SingleSelect,
  type SingleSelectOption,
} from '@/components/common/SingleSelect';
import { Checkbox } from '@/components/ui/checkbox';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import {
  useIsModuleEnabled,
  ProductModule,
} from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useCan } from '@/hooks/useCan';
import { Input } from '@/components/ui/input';
import { useToast } from '@/context/ToastContext';
import { Spinner } from '@/components/ui/spinner';
import { AuthContext } from '@/context/auth.context';
import { useGenerateAuditReport } from '@/api/rpc/hooks/audit-reports/useGenerateAuditReport';
import { subDays, startOfDay } from 'date-fns';
import { useContext } from 'react';
import type { DateRange } from 'react-day-picker';

/**
 * Maps product modules to the RBAC subjects that gate audit report access.
 * A module appears in the dropdown when:
 *  1. The org has the module enabled
 *  2. The user has Action.Report on at least one of the module's subjects
 *
 * Add new modules here as providers are implemented.
 */
const AUDIT_MODULE_CONFIG: {
  module: ProductModule;
  auditModule: AuditModule;
  label: string;
  subjects: Subject[];
}[] = [
  {
    module: ProductModule.ORDER_MANAGEMENT,
    auditModule: AuditModule.OMS,
    label: 'Order Management' as const,
    subjects: [
      Subject.SalesOrder,
      Subject.SalesOrderDraft,
      Subject.PurchaseOrder,
    ],
  },
];

const FORMAT_OPTIONS = [
  { id: 'csv', label: 'CSV' as const },
  { id: 'pdf', label: 'PDF' as const },
] as const;

type ExportFormat = (typeof FORMAT_OPTIONS)[number]['id'];

const DATE_PRESETS = [
  { label: 'Last 7 days' as const, days: 7 },
  { label: 'Last 30 days' as const, days: 30 },
  { label: 'Last 60 days' as const, days: 60 },
  { label: 'Last 90 days' as const, days: 90 },
] as const;

/**
 * Hook that returns the list of audit-reportable modules the current user can access.
 * Intersects org-level module enablement with user-level RBAC.
 */
function useAvailableAuditModules(): {
  options: SingleSelectOption[];
  isLoading: boolean;
} {
  const { isModuleEnabled, isLoading: modulesLoading } = useIsModuleEnabled();

  // Check report permissions for OMS subjects
  const [canReportSalesOrder, { isLoading: l1 }] = useCan(
    Action.Report,
    Subject.SalesOrder,
  );
  const [canReportSalesOrderDraft, { isLoading: l2 }] = useCan(
    Action.Report,
    Subject.SalesOrderDraft,
  );
  const [canReportPurchaseOrder, { isLoading: l3 }] = useCan(
    Action.Report,
    Subject.PurchaseOrder,
  );

  const permissionsBySubject: Record<string, boolean> = {
    [Subject.SalesOrder]: canReportSalesOrder,
    [Subject.SalesOrderDraft]: canReportSalesOrderDraft,
    [Subject.PurchaseOrder]: canReportPurchaseOrder,
  };

  const isLoading = modulesLoading || l1 || l2 || l3;

  const options: SingleSelectOption[] = AUDIT_MODULE_CONFIG.filter(
    ({ module, subjects }) =>
      isModuleEnabled(module) && subjects.some((s) => permissionsBySubject[s]),
  ).map(({ auditModule, label }) => ({
    id: auditModule,
    label,
  }));

  return { options, isLoading };
}

export function AuditReportsPage() {
  const { options: moduleOptions, isLoading } = useAvailableAuditModules();
  const { currentUser } = useContext(AuthContext);
  const [selectedModule, setSelectedModule] =
    useState<SingleSelectOption | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedFormats, setSelectedFormats] = useState<Set<ExportFormat>>(
    new Set(),
  );
  const [soNumber, setSoNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const { showToast } = useToast();
  const gt = useGT();
  const generateMutation = useGenerateAuditReport();

  const availableOrgs = currentUser?.availableOrganizations ?? [];
  const hasMultipleOrgs = availableOrgs.length > 1;
  const orgOptions: SingleSelectOption[] = availableOrgs.map((org) => ({
    id: org.id,
    label: org.name,
  }));
  const defaultOrg = orgOptions.find(
    (o) => o.id === currentUser?.organizationId,
  );
  const [selectedOrgOverride, setSelectedOrgOverride] =
    useState<SingleSelectOption | null>(null);
  const selectedOrg = selectedOrgOverride ?? defaultOrg ?? null;

  const toggleFormat = (format: ExportFormat) => {
    setSelectedFormats((prev) => {
      const next = new Set(prev);
      if (next.has(format)) {
        next.delete(format);
      } else {
        next.add(format);
      }
      return next;
    });
  };

  const parsedSoNumbers = useMemo(
    () =>
      soNumber
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [soNumber],
  );
  const parsedPoNumbers = useMemo(
    () =>
      poNumber
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    [poNumber],
  );

  const hasEntityFilter =
    parsedSoNumbers.length > 0 || parsedPoNumbers.length > 0;
  const hasDateRange = dateRange?.from != null && dateRange?.to != null;

  const canGenerate =
    selectedModule !== null &&
    (hasDateRange || hasEntityFilter) &&
    selectedFormats.size > 0 &&
    !generateMutation.isPending;

  const handleGenerate = () => {
    if (!selectedModule || (!hasDateRange && !hasEntityFilter)) return;

    generateMutation.mutate(
      {
        module: selectedModule.id as AuditModule,
        ...(selectedOrg ? { organizationId: selectedOrg.id } : {}),
        formats: [...selectedFormats],
        filters: {
          ...(dateRange?.from && dateRange?.to
            ? {
                dateRange: {
                  from: dateRange.from.toISOString(),
                  to: dateRange.to.toISOString(),
                },
              }
            : {}),
          ...(parsedSoNumbers.length > 0
            ? { salesOrderNumbers: parsedSoNumbers }
            : {}),
          ...(parsedPoNumbers.length > 0
            ? { purchaseOrderNumbers: parsedPoNumbers }
            : {}),
        },
      },
      {
        onError: (error) => {
          console.error('Failed to generate audit report:', error);
          showToast({
            message: gt('Failed to generate audit report', {
              context: 'error, generate report failed',
            }),
            color: 'error',
            icon: 'error',
          });
        },
      },
    );
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl space-y-6 py-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            <T context="page title">Audit Reports</T>
          </h1>
          <p className="text-muted-foreground text-sm">
            <T context="page subtitle">
              Generate audit trail reports across product modules.
            </T>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              <T context="card title">Generate Report</T>
            </CardTitle>
            <CardDescription>
              <T context="card description">
                Select a module and export format, then specify a date range or
                filter by SO/PO number to generate an audit trail report.
              </T>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label>
                <T context="form label">Module</T>
              </Label>
              <SingleSelect
                options={moduleOptions}
                value={selectedModule}
                onChange={setSelectedModule}
                placeholder={
                  isLoading
                    ? gt('Loading modules...', { context: 'loading' })
                    : gt('Select a module...', { context: 'placeholder' })
                }
                disabled={isLoading}
                fullWidth
              />
            </div>

            {hasMultipleOrgs && (
              <div className="space-y-1.5">
                <Label>
                  <T context="form label">Organization</T>
                </Label>
                <SingleSelect
                  options={orgOptions}
                  value={selectedOrg}
                  onChange={setSelectedOrgOverride}
                  placeholder={gt('Select an organization...', {
                    context: 'placeholder',
                  })}
                  fullWidth
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label>
                {hasEntityFilter ? (
                  <T context="form label">Date Range (optional)</T>
                ) : (
                  <T context="form label">Date Range</T>
                )}
              </Label>
              <div className="flex flex-wrap gap-2 pb-1">
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.days}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDateRange({
                        from: startOfDay(subDays(new Date(), preset.days)),
                        to: startOfDay(new Date()),
                      })
                    }
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>
                  <T context="form label">SO Number(s) (optional)</T>
                </Label>
                <Input
                  placeholder={gt('e.g. 1234 or 1234, 5678', {
                    context: 'SO number filter placeholder in audit reports',
                  })}
                  value={soNumber}
                  onChange={(e) => setSoNumber(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>
                  <T context="form label">PO Number(s) (optional)</T>
                </Label>
                <Input
                  placeholder={gt('e.g. ABC-567 or ABC-567, DEF-890', {
                    context: 'PO number filter placeholder in audit reports',
                  })}
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>
                <T context="form label">Export Formats</T>
              </Label>
              <div className="flex items-center gap-4 pt-1">
                {FORMAT_OPTIONS.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={selectedFormats.has(opt.id)}
                      onCheckedChange={() => toggleFormat(opt.id)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <Button
              disabled={!canGenerate}
              className="w-full"
              onClick={handleGenerate}
            >
              {generateMutation.isPending && (
                <Spinner size="sm" className="mr-2" />
              )}
              {generateMutation.isPending ? (
                <T context="loading, generating">Generating...</T>
              ) : (
                <T context="action, generate report">Generate Report</T>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
